import { useInfiniteQuery, useQueryClient } from 'react-query';
import { QueryPageHookType } from '@ts/common.types';
import { useMemo, useCallback } from 'react';
import { WorkspaceType } from '@ts/userOrganization.types';
import { getAllWorkspacesApi } from '@api/userOrganization.api';

export default (all: boolean = false): QueryPageHookType<WorkspaceType> => {
  const queryClient = useQueryClient();
  const limit = useMemo(() => {
    return all ? 250 : 10;
  }, [all]);
  const key = useMemo(() => ['organization-workspaces', limit], [limit]);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery<WorkspaceType[]>(
      key,
      ({ pageParam = 0 }) => getAllWorkspacesApi(pageParam, limit),
      {
        getNextPageParam: (lastPage: WorkspaceType[], allPages) => {
          if (lastPage.length < limit) return false;

          return allPages.length;
        },
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 15 * 60 * 1000,
      }
    );

  const removeFromCacheById = (id: number) =>
    queryClient.setQueryData(key, ({ pages, pageParams }) => {
      const updatePages = pages.map((page) => page.filter((item: WorkspaceType) => item.id != id));

      return {
        pageParams,
        pages: updatePages,
      };
    });

  const addToCache = (item: WorkspaceType) =>
    queryClient.setQueryData(key, ({ pages, pageParams }) => {
      if (pages == null || pages.length === 0) {
        return {
          pageParams,
          pages: [[item]],
        };
      }

      const updatedPages = [...pages];
      updatedPages[updatedPages.length - 1] = [...updatedPages[updatedPages.length - 1], item];

      return {
        pageParams,
        pages: updatedPages,
      };
    });

  const updateInCache = (id: number, payload: WorkspaceType) =>
    queryClient.setQueryData(key, ({ pages, pageParams }) => {
      const updatePages = pages.map((page) =>
        page.map((item: WorkspaceType) => {
          if (item.id === id) return payload;
          return item;
        })
      );

      return {
        pageParams,
        pages: updatePages,
      };
    });

  const evictCacheByKey = useCallback(() => {
    queryClient.removeQueries(key);
  }, [queryClient, key]);

  const items = useMemo(() => data?.pages?.flat(), [data?.pages]);
  return {
    items,
    refetch,
    isLoading,
    isLoadingNextPage: isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    removeItem: removeFromCacheById,
    addItem: addToCache,
    updateItem: updateInCache,
    evictByKey: evictCacheByKey,
  };
};
